import { useState, useEffect } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { FolderPickerInline } from "@/components/capture/FolderPicker"
import type { FrontMatterField, RoutingRule } from "./SettingsPage"

function getCSRFToken(): string {
  const meta = document.querySelector('meta[name="csrf-token"]')
  return meta?.getAttribute("content") || ""
}

interface RuleModalProps {
  open: boolean
  onClose: () => void
  onSave: (rule: RoutingRule) => void
  fields: FrontMatterField[]
  rootPath?: string
}

export function RuleModal({ open, onClose, onSave, fields, rootPath }: RuleModalProps) {
  const [selectedFieldId, setSelectedFieldId] = useState<string>("")
  const [selectedOptionId, setSelectedOptionId] = useState<string>("")
  const [destinationFolder, setDestinationFolder] = useState("")
  const [folderPickerOpen, setFolderPickerOpen] = useState(false)
  const [saving, setSaving] = useState(false)

  const selectedField = fields.find((f) => f.id === Number(selectedFieldId))

  useEffect(() => {
    if (open) {
      setSelectedFieldId("")
      setSelectedOptionId("")
      setDestinationFolder("")
    }
  }, [open])

  useEffect(() => {
    setSelectedOptionId("")
  }, [selectedFieldId])

  async function handleSave() {
    if (!selectedFieldId || !selectedOptionId || !destinationFolder) return
    setSaving(true)

    try {
      const res = await fetch("/api/routing_rules", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": getCSRFToken(),
        },
        body: JSON.stringify({
          front_matter_field_id: Number(selectedFieldId),
          front_matter_option_id: Number(selectedOptionId),
          destination_folder: destinationFolder,
        }),
      })

      if (res.ok) {
        const saved = await res.json()
        onSave(saved)
      }
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        {folderPickerOpen ? (
          <>
            <DialogHeader>
              <DialogTitle>Choose folder</DialogTitle>
            </DialogHeader>
            <FolderPickerInline
              onSelect={(path) => {
                setDestinationFolder(path)
                setFolderPickerOpen(false)
              }}
              currentPath={destinationFolder}
              rootPath={rootPath}
            />
            <DialogFooter>
              <Button variant="outline" onClick={() => setFolderPickerOpen(false)}>
                Back
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Add Routing Rule</DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div>
                <Label>When field</Label>
                <Select value={selectedFieldId} onValueChange={setSelectedFieldId}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select a field" />
                  </SelectTrigger>
                  <SelectContent>
                    {fields.map((f) => (
                      <SelectItem key={f.id} value={String(f.id)}>
                        {f.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Has value</Label>
                <Select
                  value={selectedOptionId}
                  onValueChange={setSelectedOptionId}
                  disabled={!selectedField}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select an option" />
                  </SelectTrigger>
                  <SelectContent>
                    {selectedField?.options.map((o) => (
                      <SelectItem key={o.id} value={String(o.id)}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Save to folder</Label>
                <div className="mt-1 flex items-center gap-2">
                  <code className="flex-1 rounded bg-muted px-3 py-2 text-sm">
                    {destinationFolder || "Select a folder..."}
                  </code>
                  <Button variant="outline" size="sm" onClick={() => setFolderPickerOpen(true)}>
                    Browse
                  </Button>
                </div>
              </div>

              {selectedField && selectedOptionId && destinationFolder && (
                <div className="rounded-lg bg-muted p-3 text-sm">
                  When <span className="font-medium">{selectedField.name}</span> is{" "}
                  <span className="font-medium">
                    {selectedField.options.find((o) => o.id === Number(selectedOptionId))?.label}
                  </span>{" "}
                  → save to <code className="rounded bg-background px-1.5 py-0.5 text-xs">{destinationFolder}</code>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving || !selectedFieldId || !selectedOptionId || !destinationFolder}
              >
                {saving ? "Saving..." : "Save Rule"}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
